import { TFunction } from 'hooks/useI18n';
import { WebOffer } from 'model/model.web-offer';
import { getTranslation } from 'utils/i18n';
import { getSalaryHourlyRate, getSalaryRangeText } from './salary.service';

/**
 * Get offer title for OG metadata
 *
 * @param offer - (server-side model) to get the title from
 * @param locale - in which `Multilingual` server-side data will be retrieved
 * @returns  The offer title
 */
export function getOfferOGTitle(
  offer: WebOffer,
  t: TFunction,
  locale?: string
): string {
  const companyName = offer.heroSectionData?.companyName;
  const title = offer.heroSectionData?.jobTitle;
  const salary = getOfferOGSalary(offer);
  const equity = getOfferOGEquity(offer, locale);
  const bonus = getOfferOGBonus(offer);
  const salaryText = `[${salary}${equity ? ` + ${equity}` : ''}${
    bonus ? ` + ${bonus}` : ''
  }]`;

  return t('offer-title', {
    title,
    salary: salaryText,
    company: companyName
  });
}

/**
 * Get the salary string of an `offer`
 *
 * @param offer - (server-side model) to get the salary from
 * @returns  The offer salary
 */
export function getOfferOGSalary(offer: WebOffer): string {
  return offer.isFreelance
    ? getSalaryHourlyRate(offer)
    : getSalaryRangeText({
        id: 0,
        min: offer.heroSectionData?.salaryMin * 1000,
        max: offer.heroSectionData?.salaryMax * 1000,
        currency: offer.heroSectionData?.currency
      });
}

/**
 * Get the equity string of an `offer`
 *
 * @param offer - (server-side model) to get the equity from
 * @param i18n - i18n object to retrieve translations
 * @param locale - in which `Multilingual` server-side data will be retrieved
 * @returns  The offer equity
 */
export function getOfferOGEquity(
  offer: WebOffer,
  locale?: string
): string | null {
  const equity = offer.offerSectionData?.offer.salaryExtra;

  if (!offer.bottombarData?.equityInf && !offer.bottombarData?.equitySup) {
    return null;
  }

  const equityText = getTranslation('offer.equity', 'job-offer', locale);

  if (
    (!offer.bottombarData?.equityInf && offer.bottombarData?.equitySup) ||
    (offer.bottombarData?.equityInf && !offer.bottombarData?.equitySup)
  ) {
    return equityText;
  }

  return equity ? `${equity}% ${equityText}` : null;
}

/**
 * Get the bonus string of an `offer`
 *
 * @param offer - (server-side model) to get the bonus from
 * @param locale - locale used to get data text
 * @returns  The offer bonus
 */
export function getOfferOGBonus(
  offer: WebOffer,
  locale?: string
): string | null {
  const bonus = offer.heroSectionData?.bonus && +offer.heroSectionData?.bonus;

  return bonus
    ? `${offer.heroSectionData?.currency}${bonus}K ${getTranslation(
        'offer.bonus',
        'job-offer',
        locale
      )}`
    : null;
}

/**
 * Get the description for OG metadata
 *
 * @param offer - (server-side model) to get the title from
 * @param locale - in which `Multilingual` server-side data will be retrieved
 * @returns  The offer title
 */
export function getOfferOGDescription(
  offer: WebOffer,
  t: TFunction,
  locale?: string
): string {
  const companyName = offer.heroSectionData?.companyName;
  const title = offer.heroSectionData?.jobTitle;
  const salary = getOfferOGSalary(offer);
  const equity = getOfferOGEquity(offer, locale);
  const bonus = getOfferOGBonus(offer);
  const salaryText = `[${salary}${equity ? ` + ${equity}` : ''}${
    bonus ? ` + ${bonus}` : ''
  }]`;

  return t('offer-desc', {
    title,
    salary: salaryText,
    company: companyName
  });
}
